import { useState } from 'react';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import { CirclePlus, ExternalLink, GraduationCap, Pin } from 'lucide-react';
import { get } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/layout/PageHeader';
import { Thumbnail } from '@/components/domain/Thumbnail';
import { formatRelative } from '@/lib/time';
import { genderLabel, resolveStoreGender } from '@/lib/store-gender';
import { coachOpenHref } from '@/lib/coach';
import { storePath } from '@/lib/store';
import { needsRep, repLabel, storeRep } from '@/lib/store-rep';

type Store = {
  id: string;
  fundraiserName?: string;
  schoolName?: string;
  sportType?: string;
  sport_id?: string;
  gender?: string | null;
  sport_gender?: string | null;
  status?: string;
  storeNumberDisplay?: string;
  primaryColorName?: string;
  secondaryColorName?: string;
  primaryColor?: string;
  secondaryColor?: string;
  endDate?: string | null;
  submittedAt?: string;
  lastRolloutAt?: string;
  ghostfit_id?: string;
  store_url?: string;
  shopifyCollectionUrl?: string;
  collectionUrl?: string;
  storeUrl?: string;
  store_handle?: string;
  shopifyCollectionHandle?: string;
  logoPath?: string;
  logos?: { gcsPath?: string }[];
  salesRepName?: string | null;
  salesRepEmail?: string | null;
  repEmail?: string | null;
};

function storeThumb(s: Store): string | undefined {
  return s.logos?.find((l) => l?.gcsPath)?.gcsPath || s.logoPath;
}
function liveUrl(s: Store): string | null {
  return s.store_url || s.shopifyCollectionUrl || s.collectionUrl || s.storeUrl || null;
}
type Payload = {
  stores: Store[];
  total?: number;
  counts?: Record<string, number>;
  nextCursor?: string | null;
};

const STATUS_VARIANT: Record<string, { label: string; className: string }> = {
  pending_review:    { label: 'Pending Review',   className: 'bg-bg text-muted' },
  artwork_ready:     { label: 'Artwork Ready',    className: 'bg-primary/10 text-primary' },
  artwork_failed:    { label: 'Artwork Failed',   className: 'bg-danger/10 text-danger' },
  approved:          { label: 'Approved',         className: 'bg-warning/10 text-warning' },
  creating_products: { label: 'Building',         className: 'bg-primary/10 text-primary' },
  live:              { label: 'Live',             className: 'bg-success/10 text-success' },
  closing_soon:      { label: 'Ends soon',        className: 'bg-warning/10 text-warning' },
  shopify_error:     { label: 'Error',            className: 'bg-danger/10 text-danger' },
  live_with_errors:  { label: 'Live w/ Errors',   className: 'bg-warning/10 text-warning' },
  archived:          { label: 'Archived',         className: 'bg-bg text-muted' },
  archiving:         { label: 'Archiving',        className: 'bg-warning/10 text-warning' },
  closed:            { label: 'Closed',           className: 'bg-bg text-muted' },
  'live,live_with_errors': { label: 'Live', className: 'bg-success/10 text-success' },
};
const FALLBACK = { label: 'Unknown', className: 'bg-bg text-muted' };

const LIVE_FILTER = 'live,live_with_errors';
const STATUS_FILTERS = [LIVE_FILTER, 'live_with_errors', 'pending_review', 'artwork_ready', 'approved', 'creating_products', 'closing_soon', 'shopify_error', 'archived', 'closed'] as const;

async function fetchAllStorePages(path: string): Promise<Payload> {
  const stores: Store[] = [];
  let cursor: string | null = null;
  let total = 0;
  let counts: Record<string, number> = {};
  do {
    const href: string = cursor ? `${path}&cursor=${encodeURIComponent(cursor)}` : path;
    const page: Payload = await get<Payload>(href);
    stores.push(...(page.stores || []));
    total = page.total ?? stores.length;
    counts = page.counts || counts;
    cursor = page.nextCursor || null;
  } while (cursor && stores.length < Math.max(total, 1) && stores.length < 5000);
  return { stores, total, counts, nextCursor: null };
}

export default function ActiveStores() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<string>(LIVE_FILTER);
  const [q, setQ] = useState('');
  const [noRepOnly, setNoRepOnly] = useState(false);
  const url = `/api/active-stores?limit=2000&status=${filter}`;
  const { data, isLoading } = useSWR<Payload>(url, fetchAllStorePages);

  const noRepCount = (data?.stores || []).filter(needsRep).length;
  const filtered = (data?.stores || []).filter((s) => {
    if (noRepOnly && !needsRep(s)) return false;
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      String(s.schoolName || '').toLowerCase().includes(needle) ||
      String(s.fundraiserName || '').toLowerCase().includes(needle) ||
      String(s.sportType || '').toLowerCase().includes(needle) ||
      String(s.storeNumberDisplay || '').toLowerCase().includes(needle) ||
      String(s.ghostfit_id || '').toLowerCase().includes(needle) ||
      String(s.gender || s.sport_gender || '').toLowerCase().includes(needle)
    );
  });

  const liveCount = data?.counts?.live;
  const errorCount = data?.counts?.live_with_errors;
  const total = data?.total ?? data?.stores.length ?? 0;
  const isLiveFilter = filter === LIVE_FILTER || filter === 'live';
  const narrowed = q || noRepOnly;
  const repNote = noRepCount ? ` · ${noRepCount} with no rep on payouts` : '';
  const subtitle = isLiveFilter
    ? `${liveCount ?? total} live${errorCount ? ` · ${errorCount} with errors` : ''}${repNote}${narrowed ? ` · ${filtered.length} match` : ''} — open a store for catalog, artwork, rollouts, and orders`
    : `${narrowed ? `${filtered.length} of ` : ''}${total} in ${STATUS_VARIANT[filter]?.label || filter}${repNote} — open a store for catalog, artwork, rollouts, and orders`;

  return (
    <div className="px-7 py-6">
      <PageHeader
        title="Team Stores"
        subtitle={subtitle}
        actions={
          <Button onClick={() => navigate('/create-store')}>
            <CirclePlus className="h-4 w-4" aria-hidden /> New store
          </Button>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`rounded-full px-3 py-1 text-[11.5px] font-medium transition-colors ${filter === s ? 'bg-text text-white' : 'border border-black/[0.08] bg-surface text-muted hover:bg-bg'}`}
            >
              {STATUS_VARIANT[s]?.label || s}
            </button>
          ))}
          <button
            onClick={() => setNoRepOnly((v) => !v)}
            aria-pressed={noRepOnly}
            title="Stores payouts have no rep for: none named, or named only in the wizard's repEmail"
            className={`rounded-full px-3 py-1 text-[11.5px] font-medium transition-colors ${noRepOnly ? 'bg-danger text-white' : 'border border-danger/30 bg-surface text-danger hover:bg-danger/5'}`}
          >
            No rep{noRepCount ? ` · ${noRepCount}` : ''}
          </button>
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by school, fundraiser, sport, pin, store #…" className="max-w-[280px] flex-1" />
      </div>

      <div className="overflow-hidden rounded-[12px] border border-black/[0.05] bg-surface shadow-card">
        {isLoading ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-7 w-full" />
            <Skeleton className="h-7 w-full" />
            <Skeleton className="h-7 w-full" />
          </div>
        ) : filtered.length ? (
          filtered.map((s) => {
            const v = STATUS_VARIANT[s.status ?? ''] ?? FALLBACK;
            const href = liveUrl(s);
            return (
              <button
                key={s.id}
                onClick={() => navigate(storePath(s.id))}
                className="flex w-full items-center gap-3 border-b border-divider px-3.5 py-2.5 text-left transition-colors last:border-b-0 hover:bg-bg/60"
              >
                <Thumbnail path={storeThumb(s)} alt="store logo" className="h-8 w-8" />
                <span className="w-14 shrink-0 font-mono text-[10px] text-subtle">
                  {s.storeNumberDisplay || s.id.slice(0, 8)}
                </span>
                <div className="flex shrink-0 gap-1">
                  {s.primaryColor && <span title={s.primaryColorName || s.primaryColor} className="h-3.5 w-3.5 rounded-full border border-black/10" style={{ background: s.primaryColor }} />}
                  {s.secondaryColor && <span title={s.secondaryColorName || s.secondaryColor} className="h-3.5 w-3.5 rounded-full border border-black/10" style={{ background: s.secondaryColor }} />}
                </div>
                <span className="min-w-0 flex-1 truncate text-[12.5px]">
                  {s.schoolName || s.fundraiserName || 'Unknown'}
                  {s.sportType ? <span className="text-muted"> · {s.sportType}</span> : ''}
                </span>
                <span className="w-12 shrink-0 capitalize text-[11px] text-muted" title={resolveStoreGender(s) === 'both' ? 'One store — both does not mint a second Shopify store' : undefined}>
                  {genderLabel(resolveStoreGender(s))}
                </span>
                {s.ghostfit_id && (
                  <span title={s.ghostfit_id} className="hidden max-w-[140px] shrink-0 items-center gap-0.5 truncate font-mono text-[10px] text-subtle lg:inline-flex">
                    <Pin className="h-3 w-3" aria-hidden /> {s.ghostfit_id}
                  </span>
                )}
                <a
                  href={coachOpenHref(s.id)}
                  onClick={(e) => e.stopPropagation()}
                  className="hidden shrink-0 text-muted hover:text-primary sm:inline-flex"
                  title="Coach view"
                >
                  <GraduationCap className="h-3.5 w-3.5" aria-hidden />
                </a>
                {href && (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="hidden shrink-0 text-primary sm:inline-flex"
                    title="Open live store"
                  >
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                  </a>
                )}
                <span
                  className={`hidden w-36 shrink-0 truncate text-[11px] md:block ${needsRep(s) ? 'text-danger' : 'text-muted'}`}
                  title={storeRep(s)?.email || 'No rep on payouts'}
                >
                  {repLabel(s)}
                </span>
                <Badge className={v.className}>{v.label}</Badge>
                <span className="w-16 shrink-0 text-right text-[11px] text-muted">
                  {s.endDate ? s.endDate.slice(0, 10) : 'Open'}
                </span>
                <span className="hidden w-14 shrink-0 text-right text-[11px] text-subtle md:block" title={s.lastRolloutAt || 'no rollout'}>
                  {s.lastRolloutAt ? formatRelative(s.lastRolloutAt) : '—'}
                </span>
              </button>
            );
          })
        ) : (
          <div className="p-10 text-center text-[12px] text-muted">
            {q
              ? `No stores match “${q}” in ${STATUS_VARIANT[filter]?.label || filter}.`
              : noRepOnly
                ? `Every store in ${STATUS_VARIANT[filter]?.label || filter} has a rep.`
                : `No stores in ${STATUS_VARIANT[filter]?.label || filter}.`}
          </div>
        )}
      </div>
    </div>
  );
}
